/**
 * TestPage Component
 * 
 * Main test-taking interface with multiple phases.
 * Refactored with:
 * - Custom hook for state management (useTest)
 * - Separate screen components for each phase
 * - Clean phase-based rendering logic
 * 
 * @module pages/TestPage
 */
import React from 'react';

// Hooks
import { useTest } from '../hooks';

// Components
import {
  SectionInstructionScreen,
  PreRuleScreen,
  MemorizationScreen,
  DistractionScreen,
  QuestionScreen,
  LoadingScreen
} from '../components/test';

/**
 * TestPage - Main test interface
 */
const TestPage: React.FC = () => {
  const {
    question,
    sectionInstruction,
    selectedAnswer,
    loading,
    currentSection,
    showMemorization,
    showDistraction,
    showPreRule,
    dimensionTimeRemaining,
    canGoBack,
    setSelectedAnswer,
    handleStartSection,
    handleMemorizationComplete,
    handleDistractionComplete,
    handlePreRuleComplete,
    handleSubmit,
    handleQuit,
    handleNavigate,
    loadNextQuestion
  } = useTest();

  // Phase 1: Section Instruction
  if (sectionInstruction) {
    return (
      <SectionInstructionScreen
        instruction={sectionInstruction}
        sectionId={currentSection}
        onStart={handleStartSection}
      />
    );
  }

  // Phase 2: Pre-Question Rule
  if (showPreRule && question?.pre_question_rule) {
    return (
      <PreRuleScreen
        rule={question.pre_question_rule}
        questionId={question.question_id}
        onComplete={handlePreRuleComplete}
      />
    );
  }

  // Phase 3: Memorization
  if (showMemorization && question?.phases?.memorization) {
    return (
      <MemorizationScreen
        content={question.phases.memorization.content}
        duration={question.phases.memorization.duration_seconds}
        questionId={question.question_id}
        onComplete={handleMemorizationComplete}
      />
    );
  }

  // Phase 4: Distraction (Puzzle)
  if (showDistraction && question?.phases?.distraction) {
    return (
      <DistractionScreen
        type={question.phases.distraction.type}
        onComplete={handleDistractionComplete}
      />
    );
  }

  // Loading state
  if (loading || !question) {
    return <LoadingScreen />;
  }

  // Phase 5: Main Question
  return (
    <QuestionScreen
      question={question}
      selectedAnswer={selectedAnswer}
      loading={loading}
      canGoBack={canGoBack}
      dimensionTimeRemaining={dimensionTimeRemaining}
      sectionId={currentSection}
      onAnswerSelect={setSelectedAnswer}
      onSubmit={handleSubmit}
      onNavigate={handleNavigate}
      onQuit={handleQuit}
      onTimeExpired={loadNextQuestion}
    />
  );
};

export default TestPage;
